import {
  Avatar, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, Switch, Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BusinessIcon from "@mui/icons-material/Business";
import { updateFirmStatus } from "api/services/icaiUdin";
import { snack } from "components/toast";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideUdinFirmsPopup, selectUdinFirmsPopup } from "redux/reducers/udinSlice";
import { handleError } from "utils/handleError";

function UdinFirmsPopup() {
  const dispatch = useDispatch();
  const { showFirmsPopup, popupFirms } = useSelector(selectUdinFirmsPopup);
  const [firms, setFirms] = useState<any[]>([]);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  useEffect(() => {
    setFirms(popupFirms);
  }, [popupFirms]);

  const handleClose = () => dispatch(hideUdinFirmsPopup());

  const handleToggle = async (firm: any) => {
    const newActive = firm.active === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    setTogglingId(firm.id);
    try {
      await updateFirmStatus(firm.id, newActive);
      setFirms((prev) => prev.map((f) => f.id === firm.id ? { ...f, active: newActive } : f));
    } catch (err: any) {
      snack.error(handleError(err));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <Dialog open={showFirmsPopup} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>

      {/* Success header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0F8B83 0%, #1A2848 100%)",
          px: 4, py: 3.5, textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 56, height: 56, borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.15)", display: "flex",
            alignItems: "center", justifyContent: "center",
            mx: "auto", mb: 1.5,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 34, color: "#fff" }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 19, color: "#fff", letterSpacing: 0.2 }}>
          UDIN Portal Connected!
        </Typography>
        <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
          Toggle which firms appear in your UDIN Register &amp; Dashboard
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {firms.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={3} gap={1}>
            <BusinessIcon sx={{ fontSize: 36, color: "#B7BECB" }} />
            <Typography variant="body2" color="text.secondary">No associated firms found.</Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5}>
            {firms.map((firm) => {
              const initials = firm.firm_name
                .split(" ").filter((w: string) => w.length > 1)
                .slice(0, 2).map((w: string) => w[0]).join("").toUpperCase();
              const isActive = firm.active === "ACTIVE";
              return (
                <Box
                  key={firm.id}
                  sx={{
                    border: "1.5px solid",
                    borderColor: isActive ? "#0F8B83" : "#E6E8EC",
                    borderRadius: 2.5,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: isActive ? "#F0FAF9" : "#FAFBFC",
                    transition: "all 0.18s ease",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      sx={{
                        bgcolor: isActive ? "#0F8B83" : "#8891A3",
                        width: 40, height: 40, fontSize: 13, fontWeight: 700,
                        transition: "background 0.18s",
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1C2433" }}>
                        {firm.firm_code} &ndash; {firm.firm_name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#8891A3", mt: 0.2 }}>
                        MRN: {firm.mrn}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" flexDirection="column" alignItems="center" gap={0.25}>
                    {togglingId === firm.id ? (
                      <CircularProgress size={18} sx={{ color: "#0F8B83" }} />
                    ) : (
                      <Switch
                        checked={isActive}
                        onChange={() => handleToggle(firm)}
                        size="small"
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#0F8B83" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#0F8B83" },
                        }}
                      />
                    )}
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: isActive ? "#0F8B83" : "#B7BECB" }}>
                      {isActive ? "Included" : "Excluded"}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        <Box
          sx={{
            mt: 2, p: 1.5, bgcolor: "#F5F7FA", borderRadius: 1.5,
            border: "1px solid #E6E8EC",
          }}
        >
          <Typography sx={{ fontSize: 11, color: "#8891A3", lineHeight: 1.6 }}>
            Only included firms will appear in the UDIN Register and Dashboard. You can change this anytime from your profile settings.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          fullWidth
          sx={{
            bgcolor: "#1A2848", "&:hover": { bgcolor: "#0F8B83" },
            textTransform: "none", fontWeight: 700, fontSize: 14,
            borderRadius: 2, boxShadow: "none", py: 1.2,
            transition: "background 0.2s",
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UdinFirmsPopup;
